import { useCallback, useEffect, useState } from 'react';
import { useAnalyticsClient } from '../../utilities/analytics/analytics-web-client';

/**
 * Task session name used for correlating passkey enrollment analytics events
 * across id-frontend, manage-profile-ui, and id-authentication.
 */
export const PASSKEY_ENROLLMENT_TASK_SESSION_NAME = 'passkey-enroll';

/**
 * HTTP header name for passing the task session ID to backend services.
 */
export const TASK_SESSION_HEADER = 'X-Task-Session-Id';

type UsePasskeyEnrollmentTaskSessionReturn = {
  /**
   * Returns URL search param entries with task session ID, correctly formatted for
   * analytics-web-client on other applications (like manage-profile-ui) to parse and consume.
   *
   * Include these in URL before redirecting the user to the passkey enrollment UX on
   * manage-profile-ui.
   */
  getUrlSearchEntriesForTaskSession: () => Array<[string, string]>;
};

/**
 * Starts a task session ID to track passkey enrollments, on mount.
 *
 * Task sessions are identifiers used for tracking user activities through multiple actions and
 * pages. A common identifier will be included in all analytics events dispatched for the duration
 * of the session.
 *
 * @see https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/atlassian-analytics-pipeline/analytics-web-client/README.md
 */
export const usePasskeyEnrollmentTaskSession = (): UsePasskeyEnrollmentTaskSessionReturn => {
  const analyticsClient = useAnalyticsClient();
  const [taskSessionId, setTaskSessionId] = useState<string | undefined>();

  useEffect(() => {
    if (taskSessionId) return;

    const awc = analyticsClient.getInstance();

    const sessionId = awc.task.createTaskSession(PASSKEY_ENROLLMENT_TASK_SESSION_NAME);

    setTaskSessionId(sessionId);
  }, [taskSessionId, analyticsClient]);

  const getUrlSearchEntriesForTaskSession = useCallback((): Array<[string, string]> => {
    if (!taskSessionId) return [];

    const taskSessionParamKey = `awc.${PASSKEY_ENROLLMENT_TASK_SESSION_NAME}`;

    return [[taskSessionParamKey, taskSessionId]];
  }, [taskSessionId]);

  return { getUrlSearchEntriesForTaskSession };
};
